import { Badge, Button, Input, Modal, Select, DateInput } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import type { BadgeVariant } from '@/components/ui'
import { Ban, Building2, Coffee, StickyNote, UserRound, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/cn'
import type { ShiftAssignment, UpdateShiftAssignmentDto } from '@/types/shift.types'
import { format, parseISO } from 'date-fns'
import {
  getAssignmentShift,
  getAssignmentShiftName,
  getAssignmentUserId,
} from '../utils/assignmentUtils'
import {
  useConfirmShiftAssignment,
  useDeleteShiftAssignment,
  useShiftAssignment,
  useUpdateShiftAssignment,
} from '../hooks/useShifts'
import { useMemo, useState } from 'react'

import { IncompleteShiftConfirmModal } from './IncompleteShiftConfirmModal'
import { PermissionGate } from '@/components/common/PermissionGate'
import { getIncompleteShiftIssues } from '../utils/incompleteShift'
import { useMe } from '@/features/settings/hooks/useMe'
import { useCompany, useCompanyMembers } from '@/features/companies/hooks/useCompanies'
import { getMemberId, getMemberLabel, getMemberSubline } from '@/features/companies/utils/memberLabel'
import { getCompanyColor } from '../utils/companyColor'

export interface EditAssignmentModalProps {
  companyId: string
  assignmentId: string | null
  onClose: () => void
  onCancel: (assignment: ShiftAssignment) => void
}

function getAssignmentTimes(assignment: ShiftAssignment): {
  start: string
  end: string
} {
  const shift = getAssignmentShift(assignment, new Map())
  const start = shift && 'startTime' in shift ? (shift.startTime ?? '') : ''
  const end = shift && 'endTime' in shift ? (shift.endTime ?? '') : ''
  return { start, end }
}

const STATUS_BADGE: Record<ShiftAssignment['status'], { variant: BadgeVariant; label: string }> = {
  scheduled: { variant: 'info', label: 'Scheduled' },
  confirmed: { variant: 'success', label: 'Confirmed' },
  cancelled: { variant: 'neutral', label: 'Cancelled' },
}

function toMinutes(time: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time)
  return match ? Number(match[1]) * 60 + Number(match[2]) : null
}

/** Shift length, wrapping past midnight. "6 hr", "7 hr 30 min". */
function getShiftDuration(start: string, end: string): { label: string; overnight: boolean } | null {
  const from = toMinutes(start)
  const to = toMinutes(end)
  if (from === null || to === null) return null
  const overnight = to <= from
  const total = overnight ? to + 1440 - from : to - from
  const hours = Math.floor(total / 60)
  const minutes = total % 60
  const label = [hours ? `${hours} hr` : '', minutes ? `${minutes} min` : ''].filter(Boolean).join(' ')
  return { label: label || '0 min', overnight }
}

function getAssignmentUserName(assignment: ShiftAssignment): string | undefined {
  const user = typeof assignment.userId === 'object' ? assignment.userId : assignment.user
  if (!user) return undefined
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
  return name || user.email
}

function DetailRow({
  icon: Icon,
  label,
  children,
  stacked = false,
}: {
  icon: LucideIcon
  label: string
  children: React.ReactNode
  stacked?: boolean
}): React.ReactElement {
  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" strokeWidth={1.75} aria-hidden />
      <div className={stacked ? 'min-w-0 flex-1 space-y-1' : 'flex min-w-0 flex-1 items-baseline justify-between gap-4'}>
        <dt className="shrink-0 text-sm text-gray-500 dark:text-gray-400">{label}</dt>
        <dd
          className={
            stacked
              ? 'whitespace-pre-line wrap-break-word text-sm text-gray-900 dark:text-gray-100'
              : 'min-w-0 text-right text-sm font-medium text-gray-900 dark:text-gray-100'
          }
        >
          {children}
        </dd>
      </div>
    </div>
  )
}

export function EditAssignmentModal({
  companyId,
  assignmentId,
  onClose,
  onCancel,
}: EditAssignmentModalProps): React.ReactElement {
  const { data: me } = useMe()
  const { data: assignment, isLoading, error } = useShiftAssignment(companyId, assignmentId)
  const deleteAssignment = useDeleteShiftAssignment()
  const confirmAssignment = useConfirmShiftAssignment()
  const updateAssignment = useUpdateShiftAssignment()
  const { data: company } = useCompany(companyId)
  const { data: membersPage } = useCompanyMembers(companyId || null, { limit: 100, page: 1 })

  const [isEditing, setIsEditing] = useState(false)
  const [editDate, setEditDate] = useState('')
  const [editStart, setEditStart] = useState('')
  const [editEnd, setEditEnd] = useState('')
  const [editUserId, setEditUserId] = useState('')
  const [editSlotName, setEditSlotName] = useState('')
  const [slotNameTouched, setSlotNameTouched] = useState(false)

  const [incompleteConfirmOpen, setIncompleteConfirmOpen] = useState(false)

  const isOwnAssignment = !!me && !!assignment && getAssignmentUserId(assignment) === me._id
  const canConfirm = isOwnAssignment && assignment?.status === 'scheduled'
  const incompleteShiftIssues = useMemo(() => getIncompleteShiftIssues(assignment), [assignment])
  const userOptions = useMemo(
    () =>
      (membersPage?.data ?? []).map((member) => {
        return {
          value: getMemberId(member),
          label: getMemberLabel(member),
          description: getMemberSubline(member),
        }
      }),
    [membersPage?.data],
  )
  const memberNameById = useMemo(
    () => new Map(userOptions.map((option) => [option.value, option.label])),
    [userOptions],
  )

  const buildAutoSlotName = (userId: string, start: string, end: string): string => {
    const member = (membersPage?.data ?? []).find((item) => getMemberId(item) === userId)
    const user = member && typeof member.userId !== 'string' ? member.userId : undefined
    const first =
      user?.firstName?.trim() ||
      user?.email?.split('@')[0] ||
      ''
    if (!first) return ''
    const codePart = company?.code?.trim() ? `-${company.code.trim()}` : ''
    const base = `${first}${codePart}`
    return start && end ? `${base} ${start}-${end}` : base
  }

  const startEditing = (): void => {
    if (!assignment) return
    const { start, end } = getAssignmentTimes(assignment)
    setEditDate(assignment.date ? assignment.date.slice(0, 10) : '')
    setEditStart(start)
    setEditEnd(end)
    setEditUserId(getAssignmentUserId(assignment))
    setEditSlotName(assignment.slotName || getAssignmentShiftName(assignment))
    setSlotNameTouched(false)
    setIsEditing(true)
  }

  const handleEditUserChange = (nextUserId: string): void => {
    setEditUserId(nextUserId)
    if (!slotNameTouched) {
      const nextName = buildAutoSlotName(nextUserId, editStart, editEnd)
      if (nextName) setEditSlotName(nextName)
    }
  }

  const runConfirm = () => {
    if (!assignmentId) return
    confirmAssignment.mutate(
      { companyId, id: assignmentId },
      {
        onSuccess: () => {
          setIncompleteConfirmOpen(false)
          onClose()
        },
      }
    )
  }

  const handleConfirm = () => {
    if (incompleteShiftIssues.length > 0) {
      setIncompleteConfirmOpen(true)
      return
    }
    runConfirm()
  }

  const handleDelete = () => {
    if (!assignmentId) return
    deleteAssignment.mutate(
      { companyId, id: assignmentId },
      {
        onSuccess: () => onClose(),
      }
    )
  }

  const handleCancelClick = () => {
    if (assignment) {
      onClose()
      onCancel(assignment)
    }
  }

  const handleSave = () => {
    if (!assignmentId || !assignment) return
    const origDate = assignment.date ? assignment.date.slice(0, 10) : ''
    const { start: origStart, end: origEnd } = getAssignmentTimes(assignment)
    const payload: UpdateShiftAssignmentDto = {}
    if (editDate && editDate !== origDate) payload.date = editDate
    if (editStart && editStart !== origStart) payload.startTime = editStart
    if (editEnd && editEnd !== origEnd) payload.endTime = editEnd
    const origUserId = getAssignmentUserId(assignment)
    if (editUserId && editUserId !== origUserId) payload.userId = editUserId
    if (editSlotName.trim() && editSlotName.trim() !== (assignment.slotName ?? '')) {
      payload.slotName = editSlotName.trim()
    }
    if (Object.keys(payload).length === 0) {
      setIsEditing(false)
      return
    }
    updateAssignment.mutate(
      { companyId, id: assignmentId, payload },
      { onSuccess: () => setIsEditing(false) }
    )
  }

  if (!assignmentId) return <></>

  const showActions = Boolean(!isLoading && assignment)
  const canEdit = !!assignment && assignment.status !== 'cancelled'
  // Mirrors the server rule: free edits before clock-in; afterwards only the end
  // time (any future end while the session is open, any correction after clock-out).
  const attendanceState = assignment?.attendanceState ?? 'none'
  const scheduleLocked = attendanceState !== 'none'

  return (
    <Modal
      open={!!assignmentId}
      onClose={onClose}
      title="Shift assignment"
      className="max-w-md"
      footer={
        showActions && assignment ? (
          isEditing ? (
            <>
              <Button
                type="button"
                variant="primary"
                onClick={handleSave}
                loading={updateAssignment.isPending}
              >
                Save changes
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setIsEditing(false)}
                disabled={updateAssignment.isPending}
              >
                Discard
              </Button>
            </>
          ) : (
            <>
              <PermissionGate module="shifts" action="delete">
                <Button
                  type="button"
                  variant="danger"
                  onClick={handleDelete}
                  loading={deleteAssignment.isPending}
                  className="mr-auto"
                >
                  Delete
                </Button>
              </PermissionGate>
              {canConfirm && (
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleConfirm}
                  loading={confirmAssignment.isPending}
                >
                  Confirm
                </Button>
              )}
              <PermissionGate module="shifts" action="update">
                {canEdit && (
                  <Button type="button" variant="secondary" onClick={startEditing}>
                    Edit
                  </Button>
                )}
              </PermissionGate>
              <PermissionGate module="shifts" action="update">
                {assignment.status !== 'cancelled' && (
                  <Button type="button" variant="secondary" onClick={handleCancelClick}>
                    Cancel assignment
                  </Button>
                )}
              </PermissionGate>
              <Button type="button" variant="ghost" onClick={onClose}>
                Close
              </Button>
            </>
          )
        ) : null
      }
    >
      {isLoading && (
        <SectionLoader variant="detail" label="Loading assignment" />
      )}
      {error && <p className="text-sm text-red-600 dark:text-red-400">{error.message}</p>}
      {!isLoading &&
        assignment &&
        (isEditing ? (
          <div className="space-y-4">
            <p className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-800 dark:bg-blue-950/30 dark:text-blue-200">
              {attendanceState === 'none'
                ? 'Editing is allowed until the employee clocks in. Changes also move the shift’s pre-start alert to the new time.'
                : attendanceState === 'active'
                  ? 'The employee is clocked in, so only the end time can change, and it must be later than now. They will be notified of the new end time.'
                  : 'The employee has clocked out, so only the end time can be corrected. The employee is not notified.'}
            </p>
            <DateInput
              label="Date"
              value={editDate}
              onChange={(e) => setEditDate(e.target.value)}
              disabled={scheduleLocked}
            />
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Assigned user
              </label>
              <Select
                options={userOptions}
                value={editUserId}
                onChange={handleEditUserChange}
                searchable
                searchPlaceholder="Search members..."
                disabled={scheduleLocked}
              />
            </div>
            <Input
              label="Slot name"
              value={editSlotName}
              onChange={(e) => {
                setSlotNameTouched(true)
                setEditSlotName(e.target.value)
              }}
            />
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Start time"
                type="time"
                value={editStart}
                onChange={(e) => setEditStart(e.target.value)}
                disabled={scheduleLocked}
              />
              <Input
                label="End time"
                type="time"
                value={editEnd}
                onChange={(e) => setEditEnd(e.target.value)}
              />
            </div>
          </div>
        ) : (
          (() => {
            const { start, end } = getAssignmentTimes(assignment)
            const duration = getShiftDuration(start, end)
            const status = STATUS_BADGE[assignment.status]
            const userName = getAssignmentUserName(assignment) ?? memberNameById.get(getAssignmentUserId(assignment))
            const companyAccent = getCompanyColor(companyId, company?.color).dot
            const breakMinutes = assignment.totalBreakMinutes ?? 0
            const isCancelled = assignment.status === 'cancelled'
            return (
              <div className="space-y-5">
                <header className="space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <h3
                      className={cn(
                        'min-w-0 wrap-break-word text-[17px] font-semibold leading-snug tracking-[-0.01em] text-gray-900 text-balance dark:text-gray-50',
                        isCancelled && 'text-gray-500 line-through decoration-gray-400/70 dark:text-gray-400'
                      )}
                    >
                      {getAssignmentShiftName(assignment)}
                    </h3>
                    <Badge variant={status.variant} dot className="mt-0.5 shrink-0">
                      {status.label}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {format(parseISO(assignment.date), 'EEEE, d MMMM yyyy')}
                    </p>
                    {(start || end) && (
                      <p className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-gray-900 dark:text-gray-50">
                        <span className="text-2xl font-semibold tabular-nums tracking-[-0.02em]">
                          {start || '—'}
                          <span className="px-1.5 font-normal text-gray-300 dark:text-gray-600">–</span>
                          {end || '—'}
                        </span>
                        {duration && (
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {duration.label}
                            {duration.overnight && ' · ends next day'}
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                </header>

                <dl className="divide-y divide-gray-200/70 overflow-hidden rounded-xl border border-gray-200/80 bg-gray-50/70 dark:divide-gray-700/60 dark:border-gray-700/70 dark:bg-gray-800/40">
                  <DetailRow icon={Building2} label="Company">
                    <span className="inline-flex max-w-full items-center gap-2">
                      <span
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: companyAccent }}
                        aria-hidden
                      />
                      <span className="truncate">{company?.name ?? '—'}</span>
                      {company?.code && (
                        <span className="shrink-0 text-xs font-normal text-gray-500 dark:text-gray-400">
                          {company.code}
                        </span>
                      )}
                    </span>
                  </DetailRow>
                  {userName && (
                    <DetailRow icon={UserRound} label="Employee">
                      <span className="block truncate">{userName}</span>
                    </DetailRow>
                  )}
                  {breakMinutes > 0 && (
                    <DetailRow icon={Coffee} label="Breaks">
                      <span className="tabular-nums">{breakMinutes} min</span>
                    </DetailRow>
                  )}
                  {isCancelled && assignment.cancellationReason && (
                    <DetailRow icon={Ban} label="Cancellation reason" stacked>
                      {assignment.cancellationReason}
                    </DetailRow>
                  )}
                  {assignment.notes && (
                    <DetailRow icon={StickyNote} label="Notes" stacked>
                      {assignment.notes}
                    </DetailRow>
                  )}
                </dl>
              </div>
            )
          })()
        ))}
      <IncompleteShiftConfirmModal
        open={incompleteConfirmOpen}
        issues={incompleteShiftIssues}
        onClose={() => setIncompleteConfirmOpen(false)}
        onConfirm={runConfirm}
        confirmLoading={confirmAssignment.isPending}
      />
    </Modal>
  )
}

EditAssignmentModal.displayName = 'EditAssignmentModal'
